import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users as UsersIcon, ShieldCheck, Search } from 'lucide-react';
import { useT } from '@/store/i18n';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Checkbox } from '@/components/ui/Checkbox';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import type { Role } from '@/types';

interface GroupSummary {
  id: number;
  name: string;
  code: string;
  description?: string;
  member_count: number;
  member_names?: string;
  question_count: number;
  is_active: boolean;
}

interface MemberRow {
  id: number;
  username: string;
  full_name: string;
  email?: string;
  role: Role;
  role_name?: string;
}

interface MembersResponse {
  group: { id: number; name: string; code: string };
  members: MemberRow[];
  available_users: MemberRow[];
}

export function SubjectPermissions() {
  const t = useT();
  const [editingGroup, setEditingGroup] = useState<GroupSummary | null>(null);

  const groups = useQuery({
    queryKey: ['subject-groups-summary'],
    queryFn: async () => {
      const r = await api<{ data?: { groups?: GroupSummary[] } }>('subject-groups/index.php');
      return r.data?.groups ?? [];
    },
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('subjperm.title')}
        description={t('subjperm.desc')}
      />

      {groups.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-5">
              <Skeleton className="h-5 w-1/2 mb-2" />
              <Skeleton className="h-3 w-3/4 mb-3" />
              <Skeleton className="h-8 w-full" />
            </Card>
          ))}
        </div>
      ) : groups.error ? (
        <Card><EmptyState title={t('subjperm.load_fail')} description={(groups.error as Error).message} /></Card>
      ) : (groups.data ?? []).length === 0 ? (
        <Card><EmptyState title={t('subjperm.empty')} description={t('subjperm.empty_desc')} /></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(groups.data ?? []).map((g) => {
            const names = (g.member_names ?? '').split(',').map((s) => s.trim()).filter(Boolean);
            return (
              <Card key={g.id} className="p-5 flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-slate-900 truncate">{g.name}</h3>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono">{g.code}</div>
                  </div>
                  <Badge tone={g.member_count === 0 ? 'warning' : 'success'}>
                    {g.member_count} {t('subjperm.people_unit')}
                  </Badge>
                </div>
                {g.description && (
                  <p className="text-xs text-slate-500 line-clamp-2 mb-3">{g.description}</p>
                )}

                <div className="flex flex-wrap gap-1 mb-3 min-h-[1.5rem]">
                  {names.length === 0 ? (
                    <span className="text-xs text-slate-400">{t('subjperm.no_members')}</span>
                  ) : (
                    <>
                      {names.slice(0, 5).map((name, i) => (
                        <Badge key={i} tone="brand">{name}</Badge>
                      ))}
                      {names.length > 5 && <Badge tone="neutral">+{names.length - 5}</Badge>}
                    </>
                  )}
                </div>

                {g.question_count > 0 && (
                  <div className="text-[11px] text-slate-400 mb-3">
                    {g.question_count.toLocaleString('th-TH')} {t('subjperm.questions_count')}
                  </div>
                )}

                <div className="mt-auto pt-3 border-t border-slate-100">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<UsersIcon className="w-4 h-4" />}
                    className="w-full"
                    onClick={() => setEditingGroup(g)}
                  >
                    {t('subjperm.manage')}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ManageMembersModal
        group={editingGroup}
        onClose={() => setEditingGroup(null)}
      />
    </div>
  );
}

function ManageMembersModal({ group, onClose }: { group: GroupSummary | null; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [originalPicked, setOriginalPicked] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');

  const groupId = group?.id;

  const detail = useQuery({
    queryKey: ['group-members', groupId],
    queryFn: async () => {
      const r = await api<{ data?: MembersResponse } & MembersResponse>(`subject-groups/members.php?group_id=${groupId}`);
      return r.data ?? r;
    },
    enabled: !!groupId,
  });

  // Initialise picked when modal opens
  useEffect(() => {
    if (!detail.data) return;
    const members = detail.data.members ?? [];
    const ids = new Set(members.map((m) => m.id));
    setPicked(ids);
    setOriginalPicked(new Set(ids));
  }, [detail.data]);

  const allUsers: MemberRow[] = detail.data
    ? [...(detail.data.members ?? []), ...(detail.data.available_users ?? [])]
    : [];

  const filtered = search
    ? allUsers.filter((u) => {
        const s = search.toLowerCase();
        return (
          u.full_name?.toLowerCase().includes(s) ||
          u.username?.toLowerCase().includes(s) ||
          u.email?.toLowerCase().includes(s)
        );
      })
    : allUsers;

  const save = useMutation({
    mutationFn: async () => {
      if (!groupId) return;
      const toAdd = [...picked].filter((id) => !originalPicked.has(id));
      const toRemove = [...originalPicked].filter((id) => !picked.has(id));

      if (toAdd.length > 0) {
        await api('subject-groups/members.php', {
          method: 'POST',
          json: { group_id: groupId, user_ids: toAdd },
        });
      }
      if (toRemove.length > 0) {
        await api('subject-groups/members.php', {
          method: 'DELETE',
          json: { group_id: groupId, user_ids: toRemove },
        });
      }
    },
    onSuccess: () => {
      toast.success(t('subjperm.success_save'));
      qc.invalidateQueries({ queryKey: ['subject-groups-summary'] });
      qc.invalidateQueries({ queryKey: ['group-members'] });
      onClose();
    },
    onError: (e: Error) => toast.error(t('error.save_failed'), e.message),
  });

  const changes = (() => {
    const added = [...picked].filter((id) => !originalPicked.has(id)).length;
    const removed = [...originalPicked].filter((id) => !picked.has(id)).length;
    return { added, removed, total: added + removed };
  })();

  return (
    <Modal
      open={!!group}
      onClose={onClose}
      title={group ? `${t('subjperm.modal_title')}: ${group.name}` : ''}
      description={t('subjperm.modal_desc')}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('btn.cancel')}</Button>
          <Button
            loading={save.isPending}
            disabled={changes.total === 0}
            onClick={() => save.mutate()}
            leftIcon={<ShieldCheck className="w-4 h-4" />}
          >
            {changes.total === 0
              ? t('subjperm.no_changes')
              : `${t('subjperm.save_changes')} (${changes.added > 0 ? `+${changes.added}` : ''}${changes.added > 0 && changes.removed > 0 ? ' / ' : ''}${changes.removed > 0 ? `-${changes.removed}` : ''})`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          leftIcon={<Search className="w-4 h-4" />}
          placeholder={t('subjperm.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="space-y-1 max-h-[50vh] overflow-y-auto -mx-1 px-1">
          {detail.isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))
          ) : filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              {search ? t('subjperm.no_search') : t('subjperm.no_users')}
            </div>
          ) : (
            filtered.map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <Checkbox
                  checked={picked.has(u.id)}
                  onChange={() => {
                    const next = new Set(picked);
                    next.has(u.id) ? next.delete(u.id) : next.add(u.id);
                    setPicked(next);
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {u.full_name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">@{u.username}</div>
                </div>
                <Badge tone={u.role === 'academic_admin' ? 'info' : 'brand'}>
                  {u.role === 'academic_admin' ? t('role.academic_admin') : t('role.teacher')}
                </Badge>
              </label>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
